import { useState } from "react";
import { useAuth, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import {
  ChartBar,
  DownloadSimple,
  CalendarBlank,
  TrendUp,
  TrendDown,
  Car,
  Receipt,
  ShoppingCart,
  Users,
  MagnifyingGlass,
  Spinner
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const MOIS = [
  { value: "1", label: "Janvier" }, { value: "2", label: "Fevrier" },
  { value: "3", label: "Mars" }, { value: "4", label: "Avril" },
  { value: "5", label: "Mai" }, { value: "6", label: "Juin" },
  { value: "7", label: "Juillet" }, { value: "8", label: "Aout" },
  { value: "9", label: "Septembre" }, { value: "10", label: "Octobre" },
  { value: "11", label: "Novembre" }, { value: "12", label: "Decembre" }
];

const now = new Date();
const currentMonth = String(now.getMonth() + 1);
const currentYear = String(now.getFullYear());

const fmt = (val) => {
  const n = Number(val) || 0;
  return n.toLocaleString("fr-BE", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " EUR";
};

const KpiCard = ({ label, value, icon: Icon, color, trend }) => (
  <div className="card p-4 flex items-center gap-4" data-testid={`kpi-${label.replace(/\s/g, '-').toLowerCase()}`}>
    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${color}`}>
      <Icon size={20} weight="bold" className="text-white" />
    </div>
    <div className="flex-1 min-w-0">
      <p className="text-xs text-slate-500 dark:text-slate-400 truncate">{label}</p>
      <p className="text-lg font-bold text-slate-900 dark:text-slate-100">{value}</p>
    </div>
    {trend !== undefined && (
      <div className={`flex items-center gap-1 text-xs font-medium ${trend >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
        {trend >= 0 ? <TrendUp size={14} /> : <TrendDown size={14} />}
      </div>
    )}
  </div>
);

const DataTable = ({ title, color, headers, rows, emptyText }) => (
  <div className="card overflow-hidden">
    <div className={`px-4 py-2.5 ${color} text-white flex items-center gap-2`}>
      <span className="text-sm font-semibold">{title}</span>
      <span className="text-xs opacity-80 ml-auto">{rows.length} ligne(s)</span>
    </div>
    {rows.length > 0 ? (
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-200 dark:border-slate-700">
              {headers.map((h, i) => (
                <th key={i} className={`px-4 py-2 text-xs font-medium text-slate-500 dark:text-slate-400 ${i >= 2 ? 'text-right' : 'text-left'}`}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => (
              <tr key={i} className="border-b border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/50">
                {row.map((cell, j) => (
                  <td key={j} className={`px-4 py-2 ${j >= 2 ? 'text-right font-mono text-xs' : ''} text-slate-700 dark:text-slate-300`}>{cell}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    ) : (
      <p className="text-center text-sm text-slate-400 py-6">{emptyText}</p>
    )}
  </div>
);

export default function Rapports() {
  const { token } = useAuth();
  const [mois, setMois] = useState(currentMonth);
  const [annee, setAnnee] = useState(currentYear);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [data, setData] = useState(null);

  const years = [];
  for (let y = 2024; y <= now.getFullYear() + 1; y++) years.push(String(y));

  const loadData = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/rapports/data`, {
        params: { mois, annee },
        
      });
      setData(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur chargement rapport");
    } finally {
      setLoading(false);
    }
  };

  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const res = await axios.get(`${API}/rapports/pdf`, {
        params: { mois, annee },
        responseType: "blob",
        
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      const moisLabel = MOIS.find(m => m.value === mois)?.label || mois;
      a.href = url;
      a.download = `rapport_${moisLabel.toLowerCase()}_${annee}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("PDF telecharge !");
    } catch (err) {
      toast.error("Erreur telechargement PDF");
    } finally {
      setDownloading(false);
    }
  };

  const fin = data?.financier;
  const act = data?.activite;

  return (
    <div className="space-y-6" data-testid="rapports-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100">Rapports Mensuels</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400">Analysez vos performances financieres et activite</p>
        </div>
      </div>

      {/* Selector */}
      <div className="card p-4">
        <div className="flex flex-col sm:flex-row items-end gap-3">
          <div className="flex-1 min-w-[140px]">
            <Label className="form-label mb-1.5">Mois</Label>
            <Select value={mois} onValueChange={setMois}>
              <SelectTrigger data-testid="rapport-mois-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {MOIS.map(m => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="flex-1 min-w-[100px]">
            <Label className="form-label mb-1.5">Annee</Label>
            <Select value={annee} onValueChange={setAnnee}>
              <SelectTrigger data-testid="rapport-annee-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {years.map(y => <SelectItem key={y} value={y}>{y}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <Button onClick={loadData} disabled={loading} className="btn-primary" data-testid="rapport-generate-btn">
            {loading ? <Spinner size={16} className="mr-1.5 animate-spin" /> : <MagnifyingGlass size={16} className="mr-1.5" />}
            {loading ? "Chargement..." : "Generer"}
          </Button>
          {data && (
            <Button onClick={downloadPdf} disabled={downloading} variant="outline" data-testid="rapport-pdf-btn">
              {downloading ? <Spinner size={16} className="mr-1.5 animate-spin" /> : <DownloadSimple size={16} className="mr-1.5" />}
              {downloading ? "..." : "Telecharger PDF"}
            </Button>
          )}
        </div>
      </div>

      {/* Results */}
      {data && (
        <div className="space-y-6 animate-in fade-in duration-300">
          {/* KPIs */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <KpiCard label="Chiffre d'affaires" value={fmt(fin?.ca_facture)} icon={TrendUp} color="bg-emerald-500" />
            <KpiCard label="Depenses totales" value={fmt(fin?.depenses_totales)} icon={TrendDown} color="bg-red-500" />
            <KpiCard label="Marge brute" value={fmt(fin?.marge_brute)} icon={ChartBar} color={fin?.marge_brute >= 0 ? "bg-blue-500" : "bg-red-500"} />
            <KpiCard label="Resultat net" value={fmt(fin?.resultat_net)} icon={Receipt} color={fin?.resultat_net >= 0 ? "bg-emerald-600" : "bg-red-600"} />
          </div>

          {/* Activite KPIs */}
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
            <KpiCard label="Vehicules vendus" value={act?.vehicules_vendus} icon={Car} color="bg-blue-500" />
            <KpiCard label="Nouveaux vehicules" value={act?.nouveaux_vehicules} icon={Car} color="bg-slate-500" />
            <KpiCard label="Nouveaux clients" value={act?.nouveaux_clients} icon={Users} color="bg-violet-500" />
            <KpiCard label="Factures creees" value={act?.factures_creees} icon={Receipt} color="bg-amber-500" />
            <KpiCard label="Audits realises" value={act?.audits_realises} icon={MagnifyingGlass} color="bg-teal-500" />
          </div>

          {/* Detail financier */}
          <div className="card p-5">
            <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100 mb-4 flex items-center gap-2">
              <ChartBar size={16} className="text-blue-600" /> Detail financier
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="flex justify-between items-center py-2 border-b border-slate-100 dark:border-slate-800">
                <span className="text-sm text-slate-600 dark:text-slate-400">CA factures payees</span>
                <span className="text-sm font-semibold text-emerald-600">{fmt(fin?.ca_facture)}</span>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-slate-100 dark:border-slate-800">
                <span className="text-sm text-slate-600 dark:text-slate-400">Total ventes vehicules</span>
                <span className="text-sm font-semibold">{fmt(fin?.total_ventes)}</span>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-slate-100 dark:border-slate-800">
                <span className="text-sm text-slate-600 dark:text-slate-400">Achats fournisseurs</span>
                <span className="text-sm font-semibold text-orange-600">{fmt(fin?.total_achats)}</span>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-slate-100 dark:border-slate-800">
                <span className="text-sm text-slate-600 dark:text-slate-400">Frais preparation</span>
                <span className="text-sm font-semibold text-orange-600">{fmt(fin?.total_preparation)}</span>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-slate-100 dark:border-slate-800">
                <span className="text-sm text-slate-600 dark:text-slate-400">Marge brute</span>
                <span className={`text-sm font-bold ${fin?.marge_brute >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>{fmt(fin?.marge_brute)}</span>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-slate-100 dark:border-slate-800">
                <span className="text-sm text-slate-600 dark:text-slate-400">Resultat net</span>
                <span className={`text-sm font-bold ${fin?.resultat_net >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>{fmt(fin?.resultat_net)}</span>
              </div>
            </div>
          </div>

          {/* Tables detail */}
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
            <DataTable
              title="Vehicules Vendus"
              color="bg-blue-600"
              headers={["Ref.", "Vehicule", "Achat", "Vente", "Marge"]}
              rows={(data.vehicules_vendus || []).map(v => {
                const pa = v.prix_achat || 0, fr = v.frais || 0, pv = v.prix_vente || 0;
                return [v.reference_interne || "-", `${v.marque} ${v.modele}`, fmt(pa), fmt(pv), fmt(pv - pa - fr)];
              })}
              emptyText="Aucun vehicule vendu"
            />
            <DataTable
              title="Factures Payees"
              color="bg-emerald-600"
              headers={["Numero", "Client", "Montant TTC"]}
              rows={(data.factures_payees || []).map(f => [f.numero, f.client_nom || "-", fmt(f.montant_ttc)])}
              emptyText="Aucune facture payee"
            />
            <DataTable
              title="Achats Fournisseurs"
              color="bg-orange-500"
              headers={["Numero", "Fournisseur", "Montant TTC"]}
              rows={(data.achats || []).map(a => [a.numero, a.fournisseur_nom || "-", fmt(a.montant_ttc)])}
              emptyText="Aucun achat"
            />
            <DataTable
              title="Factures Impayees"
              color="bg-red-500"
              headers={["Numero", "Client", "Montant TTC"]}
              rows={(data.factures_impayees || []).map(f => [f.numero, f.client_nom || "-", fmt(f.montant_ttc)])}
              emptyText="Aucune facture impayee"
            />
          </div>
        </div>
      )}

      {!data && !loading && (
        <div className="card p-12 text-center">
          <CalendarBlank size={48} className="mx-auto text-slate-300 dark:text-slate-600 mb-4" />
          <h3 className="text-lg font-semibold text-slate-700 dark:text-slate-300 mb-1">Selectionnez une periode</h3>
          <p className="text-sm text-slate-400">Choisissez un mois et une annee, puis cliquez sur "Generer"</p>
        </div>
      )}
    </div>
  );
}
